import { DateTime } from 'luxon';
import type { Organization } from '@/hooks/useOrganizations';
import {
  SubscriptionSource,
  UserSubscriptionStatus,
  type SubscriptionPlan,
} from '@/types/gql.generated';
import { isGrantingStatus, isTerminalStatus } from './subscriptionStatus';

/**
 * The state of the subscription record funding an organization.
 *
 * Deliberately separate from *entitlement*, which is `effectiveTier` and is what
 * every gate reads. The two can disagree in both directions and both readings
 * are needed: a personal org can hold a lapsed FAMILY subscription while still
 * being PRO through `inheritedSeatTier` from another workspace, and an `ending`
 * subscription is still granting even though it won't renew.
 */
export type SubscriptionHealth =
  /** Nothing to report — never subscribed, comped, or lapsed long enough ago to stop mentioning. */
  | { state: 'none' }
  /** Granting and will renew on `renewsAt`. */
  | { state: 'active'; plan: SubscriptionPlan; renewsAt: string | null }
  | { state: 'trialing'; plan: SubscriptionPlan; renewsAt: string | null }
  /** An offline (MANUAL) grant: entitling, but no Stripe customer and no auto-renew. */
  | { state: 'manual'; plan: SubscriptionPlan; endsAt: string | null }
  /** Set to cancel. Still granting until `endsAt`; resumable in the portal. */
  | { state: 'ending'; plan: SubscriptionPlan; endsAt: string | null }
  /**
   * Payment trouble — past due, unpaid, paused or incomplete. Already defunded,
   * because `isGrantingStatus` covers only ACTIVE and TRIALING. Recoverable by
   * fixing payment, so this must never be offered a *new* subscription: the old
   * one still bills if it recovers.
   */
  | { state: 'dunning'; plan: SubscriptionPlan; endedAt: string | null }
  /** Terminal. Needs a fresh checkout, not the portal. */
  | { state: 'lapsed'; plan: SubscriptionPlan; endedAt: string | null };

/**
 * How long after a subscription *finished* we keep saying so. Past this an org
 * reports `none` and the user is treated as plainly Free — someone who canceled
 * two years ago shouldn't still be told about it every time they open settings.
 *
 * Applies only to states that are over: a terminal Stripe subscription or a
 * revoked manual grant. Payment trouble never ages out, because it isn't over.
 */
const LAPSED_NOTICE_DAYS = 60;

const isWithinNoticeWindow = (endedAt: string | null): boolean => {
  // No date to age out against. A row with no period end is unusual enough (one
  // that never billed) that explaining it beats silently dropping it.
  if (!endedAt) {
    return true;
  }

  // Luxon rather than Date.now(), matching every other date read in the app —
  // it's the clock the test suite controls, so this stays consistent with
  // formatPlanPeriodDate instead of drifting against a second clock.
  const elapsedDays = DateTime.now().diff(
    DateTime.fromISO(endedAt),
    'days'
  ).days;

  // A future date hasn't been reached yet — a negative elapsed is inside the
  // window by definition, which is the normal shape for a subscription that
  // just entered dunning mid-period.
  return elapsedDays < LAPSED_NOTICE_DAYS;
};

/**
 * Reads the health of the subscription linked to `organization`.
 *
 * Works for every org type because the shape is the same: a personal org carries
 * the user's own Plus/Family subscription, a family or business org carries the
 * workspace's. A defunded org keeps its `subscriptionId`, `plan` and
 * `currentPeriodEnd` — `defundSubscription` and `syncOrgFunding` both keep the
 * link "for revival" — which is what makes it possible to name what ended rather
 * than just showing Free.
 *
 * A plain function rather than a hook: the org is always already in hand (it
 * rides the profile query), and this way it composes inside other derivations
 * without dragging in a provider.
 */
export const getSubscriptionHealth = (
  organization: Organization | null | undefined
): SubscriptionHealth => {
  const {
    subscriptionStatus: status,
    subscriptionPlan: plan,
    subscriptionSource: source,
    subscriptionCurrentPeriodEnd: periodEnd = null,
    subscriptionCancelAtPeriodEnd: cancelAtPeriodEnd = false,
  } = organization ?? {};

  // A comped org is entitled with no subscription row behind it, so there is no
  // status to read and nothing to renew, cancel or fix.
  if (!status || !plan) {
    return { state: 'none' };
  }

  if (source === SubscriptionSource.Manual) {
    // Manual grants are written ACTIVE by hand and revoked by hand, so a
    // non-granting one has genuinely been taken away. While live they simply
    // don't auto-renew, which is why they aren't `active`.
    if (isGrantingStatus(status)) {
      return { state: 'manual', plan, endsAt: periodEnd };
    }

    // A revoked grant is finished, so it ages out like a terminal Stripe one
    // rather than offering "Reactivate" forever.
    return isWithinNoticeWindow(periodEnd)
      ? { state: 'lapsed', plan, endedAt: periodEnd }
      : { state: 'none' };
  }

  if (isGrantingStatus(status)) {
    // Still granting, but won't renew. The only way to know: Stripe leaves the
    // status `active` when cancel_at_period_end is set, and CANCELED doesn't
    // arrive until entitlement is already gone.
    if (cancelAtPeriodEnd) {
      return { state: 'ending', plan, endsAt: periodEnd };
    }

    return status === UserSubscriptionStatus.Trialing
      ? { state: 'trialing', plan, renewsAt: periodEnd }
      : { state: 'active', plan, renewsAt: periodEnd };
  }

  // Only a *finished* subscription ages out. Dunning deliberately doesn't: the
  // subscription still exists and still bills if the payment recovers, so it
  // stays actionable however long it has been stuck — and reporting `none` would
  // read as plainly Free, which is what puts a buy button in front of someone
  // who already has a subscription (see `canOfferPurchase` in Subscription.tsx).
  if (isTerminalStatus(status)) {
    return isWithinNoticeWindow(periodEnd)
      ? { state: 'lapsed', plan, endedAt: periodEnd }
      : { state: 'none' };
  }

  return { state: 'dunning', plan, endedAt: periodEnd };
};

/**
 * The single period boundary a health state carries, whichever name it goes by.
 *
 * Every variant's date comes from the same column, so a component that renders
 * both a state and a date should read them from one place — passing the org's
 * `subscriptionCurrentPeriodEnd` alongside the health lets the two disagree, and
 * `none` in particular has no date to show even when the column is set.
 */
export const subscriptionHealthDate = (
  health: SubscriptionHealth
): string | null => {
  switch (health.state) {
    case 'none':
      return null;
    case 'active':
    case 'trialing':
      return health.renewsAt;
    case 'manual':
    case 'ending':
      return health.endsAt;
    case 'dunning':
    case 'lapsed':
      return health.endedAt;
  }
};

/**
 * The plan stopped granting and the owner should be told. Narrowed so callers
 * get `plan` and `endedAt` without re-checking the state.
 */
export const isSubscriptionInterrupted = (
  health: SubscriptionHealth
): health is Extract<SubscriptionHealth, { state: 'dunning' | 'lapsed' }> =>
  health.state === 'dunning' || health.state === 'lapsed';
